import { useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/store/auth'
import { useUIStore } from '@/store/ui'
import type { Client } from '@/types/database'

// Loads admin flag + client membership into the auth store.
// Exported so Login can await it before navigating, instead of racing
// onAuthStateChange and letting AuthGuard observe a half-populated store.
export async function loadProfile(userId: string) {
  const { setClient, setIsAdmin } = useAuthStore.getState()

  const [userRes, membershipRes] = await Promise.all([
    supabase.from('users').select('is_admin').eq('id', userId).maybeSingle(),
    // Many-to-many: a user may belong to several clients, so fetch the list and
    // pick the oldest membership rather than erroring on >1 row.
    supabase
      .from('user_clients')
      .select('client_id, created_at, clients(*)')
      .eq('user_id', userId)
      .order('created_at', { ascending: true }),
  ])

  setIsAdmin(userRes.data?.is_admin ?? false)

  const memberships = (membershipRes.data ?? []) as unknown as { clients: Client | null }[]
  setClient(memberships[0]?.clients ?? null)
}

export function useAuthInit() {
  const { setSession, setClient, setIsAdmin, setLoading, reset } = useAuthStore()

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session)
      if (session) await loadProfile(session.user.id)
      setLoading(false)
    }).catch(() => {
      reset()
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session)
      if (session) {
        await loadProfile(session.user.id)
      } else {
        setClient(null)
        setIsAdmin(false)
      }
      setLoading(false)
    })

    return () => subscription.unsubscribe()
  }, [setSession, setClient, setIsAdmin, setLoading, reset])
}

// The client all data queries should read/write against.
// When an admin is impersonating a tenant, that tenant wins.
export function useEffectiveClient(): Client | null {
  const client = useAuthStore((s) => s.client)
  const viewingAs = useUIStore((s) => s.viewingAs)
  return viewingAs ?? client
}

export function useEffectiveClientId() {
  const clientId = useAuthStore((s) => s.clientId)
  const viewingAs = useUIStore((s) => s.viewingAs)
  return viewingAs?.id ?? clientId
}
